/**
 * Chart geometry — turning series into SVG path data.
 *
 * Kept free of React and of any charting library: the whole evaluation view is
 * one `<svg>`, which is both smaller than a dependency and the only way to get
 * a line that looks drawn rather than plotted.
 *
 * Coordinates are computed in the SVG's own user space; the caller decides the
 * viewBox and lets CSS scale it.
 */

/** Vertical breathing room above and below the data, as a share of its range. */
const PADDING_RATIO = 0.12;

/**
 * The value range a chart has to cover, padded so lines never touch the edge.
 * @param {{points: {value: number}[]}[]} series
 * @returns {{min: number, max: number}}
 */
export function seriesBounds(series) {
  const values = series.flatMap((line) => line.points.map((point) => point.value));
  if (values.length === 0) return { min: 0, max: 1 };

  let min = Math.min(...values);
  let max = Math.max(...values);

  // A flat line has no range to pad; give it one so it renders mid-height.
  if (min === max) {
    const nudge = Math.abs(min) * 0.1 || 1;
    return { min: min - nudge, max: max + nudge };
  }

  const padding = (max - min) * PADDING_RATIO;
  return { min: min - padding, max: max + padding };
}

/**
 * Every date that appears anywhere in the series, oldest first.
 * This is the x axis: sessions are evenly spaced by *order*, not by calendar
 * distance, so a three-week gap does not squash the rest of the chart.
 * @param {{points: {date: string}[]}[]} series
 * @returns {string[]}
 */
export function seriesDates(series) {
  const dates = new Set();
  for (const line of series) {
    for (const point of line.points) dates.add(point.date);
  }
  return [...dates].sort((a, b) => a.localeCompare(b));
}

/**
 * Map one point to SVG coordinates.
 * @param {{date: string, value: number}} point
 * @param {{dates: string[], bounds: {min: number, max: number}, width: number, height: number}} frame
 * @returns {{x: number, y: number}}
 */
export function projectPoint(point, { dates, bounds, width, height }) {
  const index = dates.indexOf(point.date);
  // A single session sits in the middle rather than pinned to the left edge.
  const span = dates.length - 1;
  const x = span <= 0 ? width / 2 : (index / span) * width;

  const range = bounds.max - bounds.min || 1;
  // SVG y grows downwards, so a high value has to become a small y.
  const y = height - ((point.value - bounds.min) / range) * height;

  return { x: round(x), y: round(y) };
}

/** Two decimals is plenty for path data and keeps the DOM readable. */
function round(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Deterministic pseudo-random in [-1, 1], derived from a seed.
 *
 * Deterministic matters: the wobble has to survive a re-render, otherwise the
 * chart shimmers every time a value changes.
 * @param {number} seed
 * @returns {number}
 */
export function jitter(seed) {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return (x - Math.floor(x)) * 2 - 1;
}

/**
 * Path data through a set of points, drawn with a slight tremor so it reads as
 * a pen line. Straight segments, because a smoothed curve invents values that
 * were never recorded.
 *
 * @param {{x: number, y: number}[]} points
 * @param {number} [wobble] peak deviation in user units; 0 draws it exactly
 * @param {number} [seed] varies the tremor between lines
 * @returns {string} the `d` attribute, or '' when there is nothing to draw
 */
export function roughPath(points, wobble = 1.2, seed = 1) {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const { x, y } = points[0];
    return `M${x} ${y}`;
  }

  const parts = [];
  points.forEach((point, index) => {
    // The endpoints stay put: a line that misses its own data looks broken.
    const edge = index === 0 || index === points.length - 1;
    const offset = edge ? 0 : wobble;
    const x = round(point.x + jitter(seed + index * 2) * offset);
    const y = round(point.y + jitter(seed + index * 2 + 1) * offset);
    parts.push(`${index === 0 ? 'M' : 'L'}${x} ${y}`);
  });
  return parts.join(' ');
}

/**
 * Tick values for the y axis, always including both bounds.
 * @param {{min: number, max: number}} bounds
 * @param {number} [count] number of intervals
 * @returns {number[]} high to low, matching how they are read off the chart
 */
export function axisTicks(bounds, count = 3) {
  const step = (bounds.max - bounds.min) / count;
  return Array.from({ length: count + 1 }, (unused, i) => round(bounds.max - step * i));
}
